import { useState } from "react";
import { Btn } from "../../../AbstractElements";
import { UncontrolledTooltip} from "reactstrap";
import Swal from "sweetalert2";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { useDataFetch } from "../../Hooks/Use-data-fetch";
import { CancelOrderModal } from "./CancelOrderModal";
import { UpdateStatusOrderModal } from "./UpdateStatusOrderModal";
import { getShortId } from "../../utils/getShortId";

export const OrderActionsCell = ({ order,showDetail=true }) => {
  const navigate = useNavigate();
  const { patchData, invalidateQuery } = useDataFetch();

  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [shippingModalOpen, setShippingModalOpen] = useState(false);
  const [shippingTracking, setShippingTracking] = useState("");
  const [shippingStatus, setShippingStatus] = useState("DELIVERED");
  const [shippingDescription, setShippingDescription] = useState("");

  const { mutateAsync: cancelOrder } = patchData(`/order/cancel`, {}, {
    onSuccess: () => {
      toast.success("Orden cancelada correctamente");
      setCancelModalOpen(false);
      setCancelReason("");
      invalidateQuery(["order"]);
    },
    onError: () => toast.error("No se pudo cancelar la orden"),
  });

  const { mutateAsync: updateShippingStatus } = patchData(`/order/status`, {}, {
    onSuccess: () => {
      toast.success("Estado actualizado correctamente");
      setShippingModalOpen(false);
      invalidateQuery(["order"]);
    },
    onError: () => toast.error("Error al actualizar estado de envío"),
  });

  const { mutateAsync: markDelivered } = patchData(`/order/markDelivered`, {}, {
    onSuccess: () => {
      toast.success("Orden marcada como entregada");
      setShippingModalOpen(false);
      invalidateQuery(["order"]);
    },
    onError: () => toast.error("Error al marcar como entregada"),
  });

  const { mutateAsync: markPaid } = patchData(`/order/markPaid`, {}, {
    onSuccess: () => {
      toast.success("Orden marcada como pagada");
      invalidateQuery(["order"]);
    },
    onError: () => toast.error("Error al marcar como pagada"),
  });

  const handleDeliveredClick = () => {
    Swal.fire({
      title: "¿Marcar como entregada?",
      text: `Confirmas que la orden ${getShortId(order.id)}... fue entregada.`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Sí, marcar como entregada",
      cancelButtonText: "Cancelar",
      confirmButtonColor: "#28a745",
    }).then(async (result) => {
      if (result.isConfirmed) await markDelivered({ id: order.id });
    });
  };

  const handleCancelClick = () => setCancelModalOpen(true);
  const handleShippingClick = () => setShippingModalOpen(true);

  const handleMarkPaidClick = () => {
    Swal.fire({
      title: "¿Marcar como pagada?",
      text: `Confirmas que la orden ${getShortId(order.id)}... fue pagada por transferencia.`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Sí, marcar como pagada",
      cancelButtonText: "Cancelar",
      confirmButtonColor: "#28a745",
    }).then(async (result) => {
      if (result.isConfirmed) await markPaid({ id: order.id });
    });
  };


  const { id, status, payment_method, delivery_type } = order;
  const isDelivered = status === "DELIVERED";
  const isPaid = status === "PAID";
  const isTransfer = payment_method === "TRANSFER";
  const isStorePickup = delivery_type === "STORE_PICKUP";
  const isDelivery = delivery_type === "DELIVERY";

  const showCancel = !isDelivered && status !== "CANCELLED";
  const showShipping = isDelivery && (isPaid || status === "PROCESSING");
  const showMarkPaid = isTransfer && (status === "PENDING_APPROVAL" || status === "PENDING_PROOF");

  const showMarkDelivered =
    !isDelivered &&
    (status === "SHIPPED" ||
      (status === "PENDING_APPROVAL" && isStorePickup && !isTransfer) ||
      isPaid);

  const btnIds = {
    detail: `detail-${id}`,
    cancel: `cancel-${id}`,
    shipping: `ship-${id}`,
    paid: `paid-${id}`,
    delivered: `delivered-${id}`,
    proof: `proof-${id}`,
  };

  return (
    <>
      <div className="d-flex gap-2">
        {/* Ver detalle */}
        {showDetail && (
          <>
            <Btn
              attrBtn={{
                color: "light",
                size: "sm",
                id: btnIds.detail,
                onClick: () => navigate(`/order/detail/${id}`),
              }}
            >
              <i className="fa fa-eye" />
            </Btn>
            <UncontrolledTooltip target={btnIds.detail}>Ver detalle</UncontrolledTooltip>
          </>
        )}

        {/* Cancelar orden */}
        {showCancel && (
          <>
            <Btn
              attrBtn={{
                color: "danger",
                size: "sm",
                id: btnIds.cancel,
                onClick: handleCancelClick,
              }}
            >
              <i className="fa fa-ban" />
            </Btn>
            <UncontrolledTooltip target={btnIds.cancel}>Cancelar orden</UncontrolledTooltip>
          </>
        )}

        {/* Actualizar envío */}
        {showShipping && (
          <>
            <Btn
              attrBtn={{
                color: "info",
                size: "sm",
                id: btnIds.shipping,
                onClick: handleShippingClick,
              }}
            >
              <i className="fa fa-truck" />
            </Btn>
            <UncontrolledTooltip target={btnIds.shipping}>Actualizar envío</UncontrolledTooltip>
          </>
        )}

        {/* Marcar como pagada */}
        {showMarkPaid && (
          <>
            <Btn
              attrBtn={{
                color: "primary",
                size: "sm",
                id: btnIds.paid,
                onClick: handleMarkPaidClick,
              }}
            >
              <i className="fa fa-credit-card" />
            </Btn>
            <UncontrolledTooltip target={btnIds.paid}>Marcar como pagada</UncontrolledTooltip>
          </>
        )}

        {/* Marcar como entregada */}
        {showMarkDelivered && (
          <>
            <Btn
              attrBtn={{
                color: "success",
                size: "sm",
                id: btnIds.delivered,
                onClick: handleDeliveredClick,
              }}
            >
              <i className="fa fa-check" />
            </Btn>
            <UncontrolledTooltip target={btnIds.delivered}>Marcar como entregada</UncontrolledTooltip>
          </>
        )}
      </div>

      {/* Modales */}
      <CancelOrderModal
        cancelModalOpen={cancelModalOpen}
        setCancelModalOpen={setCancelModalOpen}
        cancelReason={cancelReason}
        setCancelReason={setCancelReason}
        selectedOrder={order}
        cancelOrder={cancelOrder}
      />

      <UpdateStatusOrderModal
        shippingModalOpen={shippingModalOpen}
        setShippingModalOpen={setShippingModalOpen}
        shippingStatus={shippingStatus}
        setShippingStatus={setShippingStatus}
        shippingDescription={shippingDescription}
        setShippingDescription={setShippingDescription}
        shippingTracking={shippingTracking}
        setShippingTracking={setShippingTracking}
        selectedOrder={order}
        updateShippingStatus={updateShippingStatus}
        markDelivered={markDelivered}
      />
    </>
  );
};
